import { Handle, Position, type NodeProps } from 'reactflow';
import { Settings } from 'lucide-react';
import type { SimulationNodeData } from '../../../models/simulation';
import { NoDrag } from './NoDrag';
import { getNodeIcon } from './nodeCard.types';

type SimulationNodeCardData = SimulationNodeData & {
  displayUnit?: string;
  metricLabel?: string;
  metricValue?: string;
  metricUnit?: string;
  onOpenOptions?: (nodeId: string) => void;
};

export const SimulationNodeCard = ({ id, data, selected }: NodeProps<SimulationNodeCardData>) => {
  const isOutput = data.isOutput ?? false;
  const nodeType = data.type;
  const nodeClass = isOutput ? 'output' : nodeType;
  const handleClass = isOutput ? 'output' : nodeType;
  const icon = getNodeIcon(nodeType, isOutput);
  const metricLabel = data.metricLabel ?? (nodeType === 'distribution' ? 'Distribution' : 'Formula');
  const metricValue = data.metricValue ?? 'Ready';

  return (
    <div className={`simulation-node simulation-node-${nodeClass} simulation-node-compact ${selected ? 'selected' : ''}`}>
      <div className={`node-type-indicator node-type-indicator-${nodeClass}`} />
      <Handle
        type="target"
        id="in"
        position={Position.Left}
        className={`node-handle node-handle-${handleClass}`}
      />

      <div className="simulation-node-header">
        <div className="simulation-node-header-top">
          <span className="simulation-node-icon">{icon}</span>
          <div className="simulation-node-header-right">
            <NoDrag>
              <button
                type="button"
                className="node-options-button nodrag"
                onClick={(event) => {
                  event.stopPropagation();
                  data.onOpenOptions?.(id);
                }}
                title="Node Options"
              >
                <Settings size={15} />
              </button>
            </NoDrag>
          </div>
        </div>
        <span className="simulation-node-label" title={data.name}>{data.name}</span>
      </div>

      <div className="simulation-node-metric">
        <span className="simulation-node-metric-label">{metricLabel}</span>
        <span className="simulation-node-metric-value">
          {metricValue}
          {data.metricUnit && <span className="simulation-node-metric-unit"> {data.metricUnit}</span>}
        </span>
      </div>

      {data.description && (
        <p className="simulation-node-description" title={data.description}>
          <span className="simulation-node-description-text">{data.description}</span>
        </p>
      )}

      {data.displayUnit && !data.metricUnit && (
        <div className="simulation-node-unit-row">
          <span className="unit-badge nodrag" title="Unit">
            {data.displayUnit}
          </span>
        </div>
      )}

      <Handle
        type="source"
        id="out"
        position={Position.Right}
        className={`node-handle node-handle-${handleClass}`}
      />
    </div>
  );
};
